import { Badge, Group, Tooltip } from '@mantine/core';
import { IconLink } from '@tabler/icons-react';
import { formatCodeableConcept, getReferenceString, resolveId } from '@medplum/core';
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { TIMELINE_FOCUS_ITEM_QUERY_PARAM } from '@bariendo/lib/constants';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { ListStateMessage } from './shared/ListStateMessage';

interface LabItem {
  id: string;
  name: string;
  reference: string;
}

function getLabPanelName(code?: { text?: string; coding?: Array<{ display?: string }> }): string {
  if (code?.text) return code.text.toUpperCase();
  if (code) {
    const formatted = formatCodeableConcept(code);
    if (formatted) return formatted.toUpperCase();
  }
  return code?.coding?.[0]?.display?.toUpperCase() || 'UNKNOWN LAB';
}

export function LabsList() {
  const { patientContext, timelineContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const navigate = useNavigate();

  const diagnosticReports = useMemo(
    () => ('diagnosticReports' in timelineContext ? (timelineContext.diagnosticReports ?? []) : []),
    [timelineContext]
  );

  const loading = timelineContext.loading && !timelineContext.hasLoadedInitialData;
  const error = 'diagnosticReportsError' in timelineContext ? timelineContext.diagnosticReportsError : undefined;

  const labItems: LabItem[] = useMemo(() => {
    if (!diagnosticReports?.length) return [];

    const labMap = new Map<string, LabItem>();

    // diagnosticReports are already sorted by issued date descending (most recent first)
    diagnosticReports.forEach((report) => {
      const labName = getLabPanelName(report.diagnosticReport.code);
      const reference = getReferenceString(report.reference);

      if (!labMap.has(labName) && report.diagnosticReport.id && reference) {
        labMap.set(labName, {
          id: report.diagnosticReport.id,
          name: labName,
          reference,
        });
      }
    });

    return Array.from(labMap.values());
  }, [diagnosticReports]);

  const handleLabClick = (itemReference: string) => {
    const patientId = resolveId(patient);
    if (!patientId) return;
    navigate(`/Patient/${patientId}/timeline?${TIMELINE_FOCUS_ITEM_QUERY_PARAM}=${encodeURIComponent(itemReference)}`);
  };

  if (!patient || loading || error || labItems.length === 0) {
    return (
      <ListStateMessage
        patient={patient}
        loading={loading}
        error={error}
        empty={labItems.length === 0}
        emptyMessage="No labs recorded"
        errorMessagePrefix="Error loading labs"
      />
    );
  }

  return (
    <Group gap={8}>
      {labItems.map((item) => (
        <LabPill key={item.id} item={item} onClick={() => handleLabClick(item.reference)} />
      ))}
    </Group>
  );
}

function LabPill({ item, onClick }: { item: LabItem; onClick: () => void }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const isTruncated = useIsTruncated(textRef, [item.name]);

  const badge = (
    <Badge
      variant="light"
      color="blue"
      size="sm"
      style={{
        cursor: 'pointer',
        textTransform: 'uppercase',
        fontWeight: 600,
        fontSize: '11px',
        padding: '2px 8px',
        maxWidth: '200px',
        display: 'inline-flex',
        alignItems: 'center',
      }}
      rightSection={<IconLink size={12} style={{ marginLeft: 4 }} />}
      onClick={onClick}
    >
      <span
        ref={textRef}
        style={{
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.name}
      </span>
    </Badge>
  );

  return isTruncated ? (
    <Tooltip label={item.name} withArrow position="top">
      {badge}
    </Tooltip>
  ) : (
    badge
  );
}
